import type { BrandDomainType } from './artDirection'
import { isEdumiamHostname } from './featureFlags'

/** Hors navigateur (tests, rendu serveur), il n'y a pas de host : jamais Edumiam. */
const onEdumiamHost = (): boolean => typeof window !== 'undefined' && isEdumiamHostname()

// Lecture de l'URL d'entrée de la marketplace à froid, et mémoire du groupe déjà créé depuis ce
// navigateur. Cf. docs/marketplace-cold-start.md pour la grammaire complète.

/** Paramètres décrivant le profil et la sélection, relayés tels quels au serveur, qui les résout et
 * signale ceux qu'il ignore. Tout le reste de la query string est du bruit publicitaire. */
export const CATALOGUE_PARAMETERS = ['domain', 'profession', 'funder', 'themes', 'featured', 'cart'] as const

const TRACKING_PARAMETERS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'adset_id',
] as const

export type TrackingParametersType = Partial<Record<(typeof TRACKING_PARAMETERS)[number], string>> & {
  entrypoint_url: string
}

/** Query string à envoyer à l'endpoint catalogue, expurgée de tout ce qui ne le concerne pas.
 *
 * À défaut de `domain=` explicite, le chemin fait office de domaine de catalogue : sans lui, le
 * serveur sert tous les domaines confondus et `/sante/formations` proposerait Photoshop à une
 * infirmière. Un `domain=` porté par le lien reste prioritaire, c'est lui qui permet de servir le
 * catalogue Edumiam sous l'habillage Learning. */
export const buildCatalogueQuery = (
  search: URLSearchParams,
  pathname: string,
  edumiamHost: boolean = onEdumiamHost(),
): URLSearchParams => {
  const query = new URLSearchParams()
  CATALOGUE_PARAMETERS.forEach((name) => {
    const value = search.get(name)
    if (value) query.set(name, value)
  })
  if (!query.get('domain')) {
    query.set('domain', edumiamHost ? 'edumiam' : pathname.startsWith('/sante') ? 'sante' : 'learning')
  }
  return query
}

/** Attribution marketing portée par le lien, à recopier sur les Registration créées.
 *
 * `entrypoint_url` embarque l'URL complète : c'est la seule trace de ce qu'on a réellement promis au
 * visiteur, paramètres inconnus compris. Un `entrypoint_url=` explicite prime, car il désigne la page
 * partenaire d'où vient le visiteur, plus parlante que la page marketplace où il a atterri. */
export const readTrackingParameters = (search: URLSearchParams, href: string): TrackingParametersType => {
  const tracking: TrackingParametersType = { entrypoint_url: (search.get('entrypoint_url') || href).slice(0, 1024) }
  TRACKING_PARAMETERS.forEach((name) => {
    const value = search.get(name)
    if (value) tracking[name] = value.slice(0, 255)
  })
  return tracking
}

// Le groupe créé depuis ce navigateur, pour qu'un second panier s'y rattache au lieu de fragmenter le
// prospect en plusieurs dossiers. Rien d'autre ne relie deux visites : il n'existe volontairement
// aucune reprise par email côté public, qui livrerait le `global_id`, jeton d'accès au hub.
const GROUP_STORAGE_KEY = 'walter_cold_start_group_v1'

export const readRememberedGroupId = (): string | null => {
  try {
    return window.localStorage.getItem(GROUP_STORAGE_KEY)
  } catch {
    return null
  }
}

export const rememberGroupId = (globalId: string): void => {
  try {
    window.localStorage.setItem(GROUP_STORAGE_KEY, globalId)
  } catch {
    // localStorage indisponible : le visiteur repartira d'un groupe neuf à sa prochaine visite.
  }
}

export const forgetRememberedGroupId = (): void => {
  try {
    window.localStorage.removeItem(GROUP_STORAGE_KEY)
  } catch {
    // Voir `rememberGroupId`.
  }
}

/** Marque à afficher : le chemin la porte par défaut, le catalogue réellement servi tranche.
 *
 * Les deux notions ne coïncident pas -- un catalogue Edumiam s'habille en Learning -- et un lien peut
 * fort bien pointer `/sante/formations?domain=learning`. Suivre le payload évite une page santé remplie
 * de formations généralistes. */
export const resolveDisplayDomain = (
  pathname: string,
  payloadDomain: string | null | undefined,
  edumiamHost: boolean = onEdumiamHost(),
): BrandDomainType => {
  // Sur inscription.edumiam.com, tout s'habille en Edumiam : le visiteur ne doit jamais croiser Walter.
  if (edumiamHost) return 'edumiam'
  if (payloadDomain) return payloadDomain === 'sante' ? 'sante' : 'learning'
  return pathname.startsWith('/sante') ? 'sante' : 'learning'
}

/** Financeurs dont le lien enferme le catalogue : le parcours (tunnel IPERIA) en dépend, le visiteur ne
 * peut donc pas élargir à « toutes les formations ». Le financeur est alors transmis à la création. */
export const LOCKED_FUNDER_CODES = ['IPERIA']

export const isLockedFunder = (code: string | null | undefined): boolean =>
  Boolean(code && LOCKED_FUNDER_CODES.includes(code.toUpperCase()))

/** Valeurs de paramètres que le serveur a ignorées, aplaties pour la trace. */
export const flattenIgnoredParameters = (ignored: Record<string, string[]>): string[] =>
  Object.entries(ignored)
    .filter(([, values]) => values.length > 0)
    .map(([name, values]) => `${name}=${values.join(',')}`)

// Identifiant de visiteur de la marketplace : il ne sert qu'à compter les visites et à les relier à
// « Continuer » (mesure d'audience, propre à ce domaine). Absent si le stockage est bloqué : le CRM
// retombe alors sur une empreinte du jour.
const VISITOR_STORAGE_KEY = 'walter_marketplace_visitor_v1'

export const readVisitorId = (): string | null => {
  try {
    const existing = window.localStorage.getItem(VISITOR_STORAGE_KEY)
    if (existing) return existing
    if (typeof crypto === 'undefined' || typeof crypto.randomUUID !== 'function') return null
    const created = crypto.randomUUID()
    window.localStorage.setItem(VISITOR_STORAGE_KEY, created)
    return created
  } catch {
    return null
  }
}

/** Corps de `POST /registration/content/marketplace_visit/` : le profil décrit par le lien et son attribution. */
export const buildVisitPayload = (
  search: URLSearchParams,
  pathname: string,
  href: string,
  visitorId: string | null,
  edumiamHost: boolean = onEdumiamHost(),
) => {
  const query = buildCatalogueQuery(search, pathname, edumiamHost)
  return {
    visitor_id: visitorId,
    domain: query.get('domain'),
    funder: search.get('funder'),
    profession: search.get('profession'),
    themes: search.get('themes'),
    ...readTrackingParameters(search, href),
  }
}

/** Coordonnées saisies sur la marketplace (ou passées dans le lien), relayées au CRM sur la visite puis
 * sur les inscriptions créées par « Continuer ». */
export type MarketplaceIdentity = {
  first_name: string
  last_name: string
  email: string
  mobile_phone: string
}

export const EMPTY_IDENTITY: MarketplaceIdentity = { first_name: '', last_name: '', email: '', mobile_phone: '' }

/** Préremplissage depuis le lien : `first_name`, `last_name`, `email`, `phone` (ou `mobile_phone`). */
export const readIdentityFromUrl = (search: URLSearchParams): MarketplaceIdentity => ({
  first_name: (search.get('first_name') || '').trim(),
  last_name: (search.get('last_name') || '').trim(),
  email: (search.get('email') || '').trim(),
  mobile_phone: (search.get('phone') || search.get('mobile_phone') || '').trim(),
})

export const hasIdentity = (identity: MarketplaceIdentity): boolean =>
  Object.values(identity).some((value) => value.length > 0)

/** `identity=1` : le lien demande les coordonnées avant de montrer le catalogue. */
export const identityRequested = (search: URLSearchParams): boolean => ['1', 'true'].includes(search.get('identity') || '')

/** Financeurs qui prennent toute la formation en charge : le prix catalogue n'a rien à faire dans le parcours. */
const FULLY_FUNDING_CODES = ['IPERIA']

export const hidesPrice = (funderCode: string | null | undefined): boolean =>
  Boolean(funderCode && FULLY_FUNDING_CODES.includes(funderCode.toUpperCase()))

/** Avec un financeur verrouillé, on ne montre que lui : les autres financements ne concernent pas ce parcours. */
export const restrictToFunder = <T extends { eligible_funding_organisms: { code: string | null }[] }>(
  themes: T[],
  funderCode: string | null | undefined,
): T[] => {
  if (!funderCode) return themes
  const code = funderCode.toUpperCase()
  return themes.map((theme) => ({
    ...theme,
    eligible_funding_organisms: theme.eligible_funding_organisms.filter((organism) => organism.code === code),
  }))
}
